import { ArrowLeft } from "lucide-react";

const CONTENT = {
  terms: {
    title: "Terms of Service",
    effectiveDate: "March 6, 2026",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        body: `By creating an account or using ApplicantHQ (the "Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service. We may update these Terms from time to time — continued use after changes are posted constitutes acceptance.`,
      },
      {
        heading: "2. Description of Service",
        body: `ApplicantHQ is an AI-powered job application assistance platform providing: resume parsing, storage, and AI-assisted tailoring; ATS scoring; job listing scraping and storage; application tracking and analytics; and automated job application submission via browser automation. The Service does not guarantee employment or any specific outcome.`,
      },
      {
        heading: "3. Eligibility",
        body: `You must be at least 18 years old and legally authorized to work in the jurisdiction where you are applying for jobs. By using ApplicantHQ, you represent that you meet these requirements.`,
      },
      {
        heading: "4. User Accounts",
        body: `You are responsible for maintaining the confidentiality of your account credentials and all activity under your account. Notify us at support@theapplicanthq.com if you suspect unauthorized access. By signing in via Google, you authorize us to receive your name, email address, and profile picture from Google in accordance with Google's own terms.`,
      },
      {
        heading: "5. Subscription, Billing, and Payments",
        body: `ApplicantHQ offers a free trial and paid subscription tiers. Payments are processed by Stripe, Inc. — we do not store your payment card information. Paid subscriptions renew automatically on a monthly basis unless cancelled before the renewal date. Cancellation takes effect at the end of the current billing period. We do not provide prorated refunds for partial periods unless required by applicable law.`,
      },
      {
        heading: "6. Acceptable Use",
        body: `You agree not to: submit false or fraudulent information in job applications; impersonate another person or entity; circumvent any platform's anti-automation or bot-detection measures in violation of that platform's terms; or engage in any activity that violates applicable law.`,
      },
      {
        heading: "7. Job Scraping and Third-Party Content",
        body: `The Service may retrieve job listings from third-party websites on your behalf. You are solely responsible for ensuring your use of scraped content complies with the applicable site's terms of service. You agree to indemnify and hold harmless ApplicantHQ from any claims arising from your use of job scraping features in violation of any third-party site's terms.`,
      },
      {
        heading: "8. Automated Job Application Submission",
        body: `You authorize ApplicantHQ to act as your agent solely for submitting applications you direct it to submit. All representations made in submitted applications are yours — ApplicantHQ does not verify their accuracy. You are solely responsible for the content of all submitted applications. ApplicantHQ is not liable for failed submissions, missed deadlines, or errors resulting from third-party platform changes.`,
      },
      {
        heading: "9. AI-Generated Content",
        body: `AI-generated resume content is provided as a suggestion only and does not constitute professional career advice. You are responsible for reviewing all AI-generated content before submission. ATS scores are estimates based on our methodology and do not guarantee how any employer's system will evaluate your resume.`,
      },
      {
        heading: "10. Intellectual Property",
        body: `You retain ownership of all resume content and materials you upload. By uploading, you grant ApplicantHQ a limited, non-exclusive license to process and store your content solely to provide the Service. All software, design, and trademarks comprising the Service are owned by or licensed to ApplicantHQ.`,
      },
      {
        heading: "11. Disclaimers",
        body: `THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE, OR THAT ANY APPLICATION SUBMITTED WILL BE RECEIVED OR PROCESSED BY THE INTENDED EMPLOYER.`,
      },
      {
        heading: "12. Limitation of Liability",
        body: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, APPLICANTHQ SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES. OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO APPLICANTHQ IN THE TWELVE MONTHS PRECEDING THE CLAIM.`,
      },
      {
        heading: "13. Indemnification",
        body: `You agree to indemnify and hold harmless ApplicantHQ from any claims, damages, or expenses arising out of your use of the Service, your violation of these Terms, or your violation of any third-party rights.`,
      },
      {
        heading: "14. Termination",
        body: `We may suspend or terminate your account if you violate these Terms. You may terminate your account at any time by contacting support@theapplicanthq.com. Upon termination, your data will be deleted in accordance with our Privacy Policy.`,
      },
      {
        heading: "15. Governing Law",
        body: `These Terms are governed by the laws of the Commonwealth of Massachusetts. Disputes shall be resolved exclusively in state or federal courts located in Massachusetts.`,
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    effectiveDate: "March 6, 2026",
    sections: [
      {
        heading: "1. Overview",
        body: `This Privacy Policy explains what data ApplicantHQ collects, how we use it, who we share it with, and your rights. We are committed to being transparent about our data practices.`,
      },
      {
        heading: "2. Data We Collect",
        body: `Account & Identity: name, email, profile picture URL, Google OAuth ID, login timestamps.\n\nResume & Profile: contact info (name, phone, location, LinkedIn, portfolio), work authorization, work experience, education, skills, certifications, publications, and other resume sections you provide.\n\nJob Data: job title, company, location, salary, full job description text, job URL, application status, and ATS match score.\n\nBilling: subscription tier, usage counters, Stripe customer ID and subscription ID. We do not store payment card numbers.\n\nUsage Data: standard server logs including IP addresses, browser type, and page visit timestamps.`,
      },
      {
        heading: "3. How We Use Your Data",
        body: `We use your data to: provide the Service (resume tailoring, ATS scoring, job tracking); power AI features by sending resume and job data to Google Gemini API; authenticate your account; manage billing via Stripe; send transactional emails via SendGrid; and improve and debug the Service using anonymized analytics.\n\nWe do not use your data for advertising. We do not sell your data.`,
      },
      {
        heading: "4. Third-Party Services",
        body: `Google Gemini API: your resume content and job descriptions are sent to Google's Gemini API to power AI features. Depending on your applicable Google API agreement, Google may use submitted data to improve its models. We are confirming our data processing arrangement with Google and will update this policy accordingly.\n\nStripe: processes subscription payments. We store only Stripe-assigned customer and subscription identifiers.\n\nSendGrid: sends transactional emails. Your email is transmitted for this purpose.\n\nGoogle Cloud Run: our backend is hosted here. MongoDB Atlas stores your account and application data.\n\nWe do not share your data with employers, recruiters, other users, data brokers, or advertising networks.`,
      },
      {
        heading: "5. AI Processing and Your Resume Data",
        body: `When you use AI-powered features, your resume content and relevant job descriptions are transmitted to Google's Gemini API. This data leaves our servers and is processed by Google. We do not pool or aggregate resume data across users — AI features process your data individually and in isolation.`,
      },
      {
        heading: "6. Data Retention and Deletion",
        body: `We retain your data for as long as your account is active. When you delete your account, your personal data is permanently hard-deleted from our systems — this includes your profile, resume data, saved jobs, and subscription records. Deletion cannot be undone. Anonymized, non-identifiable usage statistics may be retained indefinitely. Deleted data may persist in encrypted backups for up to 30 days before being purged.\n\nTo delete your account, use the account deletion feature in your settings or contact support@theapplicanthq.com.`,
      },
      {
        heading: "7. Data Security",
        body: `We use HTTPS encryption for all data in transit, Google OAuth with token-based API access for authentication, and access controls limiting data access to necessary system components. No method of internet transmission is 100% secure — we cannot guarantee absolute security.`,
      },
      {
        heading: "8. Your Rights",
        body: `Regardless of your location, you have the right to: access a copy of your personal data; correct inaccurate data via your profile or by contacting us; delete your account and all associated data; and request a data export in machine-readable format.\n\nContact support@theapplicanthq.com to exercise any of these rights. We will respond within 30 days.\n\nNote for future EU/UK users: when ApplicantHQ becomes available in the EU or UK, we will update this policy to address GDPR requirements.`,
      },
      {
        heading: "9. Children's Privacy",
        body: `The Service is not directed to individuals under 18. We do not knowingly collect personal data from minors. Contact support@theapplicanthq.com if you believe a minor has provided us with data.`,
      },
      {
        heading: "10. Changes to This Policy",
        body: `We may update this Privacy Policy periodically. We will update the effective date and, for material changes, notify you via email. Continued use after changes are posted constitutes acceptance.`,
      },
      {
        heading: "11. Contact",
        body: `Rimsha Kayastha, operating as ApplicantHQ\nEmail: support@theapplicanthq.com\nWebsite: theapplicanthq.com`,
      },
    ],
  },
};

const LegalScreen = ({ type = "terms", navigateBack, navigateTo }) => {
  const content = CONTENT[type];

  return (
    <div className="min-h-screen bg-loading-color">
      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-md p-8">
          <h1 className="applicant-theme-text">{content.title}</h1>
          <p className="text-xs text-gray-500 mb-6">
            Effective Date: {content.effectiveDate} · Operated by ApplicantHQ
          </p>

          <div className="horizontal-line mb-6" />

          <div className="legal-sections">
            {content.sections.map((s, i) => (
              <div key={i} className="legal-section">
                <h2 className="legal-section-heading">{s.heading}</h2>
                <p className="legal-section-body">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="horizontal-line mt-8 mb-4" />
          <p className="text-xs text-gray-400 text-center">
            Questions? Contact us at{" "}
            <a
              href="mailto:support@theapplicanthq.com"
              className="text-indigo-600"
            >
              support@theapplicanthq.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalScreen;
